import {ChangeEvent, useCallback, useRef} from 'react';
import {useFormikContext} from 'formik';
import {isFunction} from 'utils/functions';
import {useFileSelectionLoading} from './fileSelectionLoading';

const attachFilePath = (file: File, path?: string): File => {
  if (!file || !path) {
    return file;
  }

  try {
    Object.defineProperty(file, 'path', {
      value: path,
      writable: true,
      configurable: true,
    });
  } catch {
    (file as File & {path?: string}).path = path;
  }

  return file;
};

export const useDragAndDrop = (name: string, deleteCallback?: () => void) => {
  const {values, setFieldValue, errors} = useFormikContext<any>();
  const fileSelectionLoading = useFileSelectionLoading();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const file = values[name];
  const error = errors[name] as string;

  const addFileClickHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newFile = event.target.files && event.target.files[0];
      if (!newFile) {
        fileSelectionLoading?.clearLoading();
        return;
      }

      const selectedPath = event.target.value;
      const fileWithPath = attachFilePath(newFile, selectedPath);
      event.target.value = '';

      if (fileSelectionLoading) {
        fileSelectionLoading.applySelectedFile(fileWithPath);
        return;
      }

      setFieldValue(name, fileWithPath);
    },
    [fileSelectionLoading, name, setFieldValue]
  );

  const cancelFileDialogHandler = useCallback(() => {
    fileSelectionLoading?.clearLoading();
  }, [fileSelectionLoading]);

  const deleteClickHandler = useCallback(() => {
    fileSelectionLoading?.clearLoading();
    setFieldValue(name, null);
    isFunction(deleteCallback) && deleteCallback();
  }, [deleteCallback, fileSelectionLoading, name, setFieldValue]);

  const openFileDialogClickHandler = useCallback(() => {
    // Prefer the stable picker owned by FileSelectionLoadingProvider so the
    // loading overlay cannot remount the input while the OS dialog is open.
    if (fileSelectionLoading?.openFilePicker) {
      fileSelectionLoading.openFilePicker();
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [fileSelectionLoading]);

  return {
    file,
    error,
    fileInputRef,
    addFileClickHandler,
    cancelFileDialogHandler,
    deleteClickHandler,
    openFileDialogClickHandler,
  };
};
